import React, { useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useFormik } from "formik";
import * as Yup from "yup";
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";
import { CategoryService } from "../../../services/user/category.services";


const AddCategoryComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const categoryId = location.state?.categoryId;
  const isEditMode = !!categoryId;

  const formik = useFormik({
    initialValues: {
      categoryName: "",
      categoryDescription: "",
      isActive: true,
    },

    validationSchema: Yup.object({
      categoryName: Yup.string()
        .required("Please enter a valid name"),
      categoryDescription: Yup.string()
        .required("Please enter a valid description"),
    }),

    onSubmit: async (values, { resetForm }) => {
      try {
        if (isEditMode) {
          await CategoryService.updateCategoryById(categoryId, values)
            .then(response => {
              if (response) {
                navigate('/category');
              }
            })
        }
        else {
          await CategoryService.addNewCategory(values)
            .then(response => {
              if (response) {
                navigate('/category')
              }
            })
        }
      }
      catch (error) {
        handleNotify('Error while add Category', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT)
      }
    },
  });

  useEffect(() => {
    if (isEditMode) {
      const fetchCategoryDetail = async () => {
        try {
          const data = await CategoryService.getCategoryById(categoryId);
          if (data) {
            formik.setValues({
              categoryName: data?.categoryName,
              categoryDescription: data?.categoryDescription,
              isActive: data?.isActive === true ? true : false,
            })
          }
        }
        catch (error) {
          handleNotify('Error fetching category by id', error, TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT)

        }
      };
      fetchCategoryDetail();
    }
  }, []);

  const handleBackClick = () => {
    navigate('/category');
  };

  return (
    <>
      <div className="page-wrapper p-4 m-0">
        <div className="page-content">
          <div className="page-breadcrumb d-flex align-items-center mb-3">
            <div
              className="breadcrumb-title pe-3 text-primary fw-bold fs-4 cursor-pointer"
              onClick={handleBackClick}
            >
              Categories</div>
            <div className="ps-3">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb mb-0 p-0">
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEditMode ? "Edit Category" : "New Category"}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="ms-auto">
              <button
                type="button"
                className="btn btn-outline-secondary me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                onClick={handleBackClick}
              >
                Back
              </button>
            </div>
          </div>

          <div className="card">
            <div
              className="card-body"
              style={{
                borderTop: '4px solid #007bff',
                borderRadius: '5px 5px 0 0',
                paddingLeft: '50px',
                paddingRight: '50px',
              }}
            >
              <form onSubmit={formik.handleSubmit} className="mt-4">
                {/* Name Field */}
                <div className="row mb-4">
                  <div className="col-md-6 col-12">
                    <label htmlFor="categoryName" className="form-label d-flex">
                      Name <span className="text-danger">*</span>:
                    </label>
                    <input
                      type="text"
                      id="categoryName"
                      name="categoryName"
                      className={`form-control ${formik.touched.categoryName && formik.errors.categoryName ? 'is-invalid' : ''}`}
                      placeholder="Enter Category Name"
                      value={formik.values.categoryName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.categoryName && formik.errors.categoryName && (
                      <div className="invalid-feedback d-flex">{formik.errors.categoryName}</div>
                    )}
                  </div>
                </div>

                {/* Description Field */}
                <div className="row mb-4">
                  <div className="col-md-6 col-12">
                    <label htmlFor="categoryDescription" className="form-label d-flex">
                      Description <span className="text-danger">*</span>:
                    </label>
                    <textarea
                      id="categoryDescription"
                      name="categoryDescription"
                      className={`form-control ${formik.touched.categoryDescription && formik.errors.categoryDescription ? 'is-invalid' : ''}`}
                      placeholder="Enter Category Description"
                      value={formik.values.categoryDescription}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.categoryDescription && formik.errors.categoryDescription && (
                      <div className="invalid-feedback d-flex">{formik.errors.categoryDescription}</div>
                    )}
                  </div>
                </div>

                {/* Status Radio Buttons */}
                <div className="d-flex align-items-center mb-4">
                  <label className="form-label me-3">Isactive:</label>
                  <div className="form-check form-check-inline me-3">
                    <input
                      type="radio"
                      id="active"
                      name="isActive"
                      className="form-check-input"
                      value={true}
                      checked={formik.values.isActive === true}
                      onChange={() => formik.setFieldValue("isActive", true)}
                    />
                    <label className="form-check-label" htmlFor="active">
                      Active
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      type="radio"
                      id="inactive"
                      name="isActive"
                      className="form-check-input"
                      value={false}
                      checked={formik.values.isActive === false}
                      onChange={() => formik.setFieldValue("isActive", false)}
                    />
                    <label className="form-check-label" htmlFor="inactive">
                      Inactive
                    </label>
                  </div>
                </div>

                {/* Submit */}
                <hr />
                <div className="d-flex flex-column flex-md-row mt-4 justify-content-end">
                  <button
                    type="button"
                    className="btn btn-outline-secondary mb-2 mb-md-0 me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                    onClick={handleBackClick}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary px-4 py-2 rounded-3 shadow-sm hover-effect"
                  >
                    {isEditMode ? "Update" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddCategoryComponent;
